import { SCENE, STREET_SPAWNS } from '../config.js';
import { gameState } from '../state/gameState.js';
import { showActionsModal } from './Modal.js';
import { showToast } from './Toast.js';
import { messageFor } from '../api/errors.js';

// The panel you get by walking up to one of your own cars in the garage (BackyardScene).
// It is the car's dashboard: what it is, how full it is, how far it will go — plus the two
// things you can only do at home: drive out, and (for an EV) pick the charging limit.
// Everything else (refuelling, repairs, selling) lives at the station / dealer.

const FUEL_LINE = { pb95: '{fuel} Pb95', on: '{fuel} ON', ev: '{plug} elektryczny' };

// "2 h 15 min" / "15 min" / "gotowe" — the same shape as quizLogic's formatCooldown but
// without its "za " prefix, because it is read as "…do 80 %: 2 h 15 min".
export function formatEta(seconds) {
  const s = Math.max(0, Math.round(Number(seconds) || 0));
  if (s < 60) return 'gotowe';
  const h = Math.floor(s / 3600);
  const m = Math.floor((s % 3600) / 60);
  return h > 0 ? `${h} h ${m} min` : `${m} min`;
}

// Polish decimals use a comma. `digits` 0 for whole numbers (money, %, km).
function num(value, digits = 0) {
  return (Number(value) || 0).toFixed(digits).replace('.', ',');
}

function grouped(value) {
  return String(Math.round(Number(value) || 0)).replace(/\B(?=(\d{3})+(?!\d))/g, ' ');
}

// Always read the car back out of the live snapshot: the record the caller captured is a
// frame old, and every action here (charge limit, leaving) rewrites it.
function currentRecord(car) {
  const id = typeof car === 'string' ? car : car?.id;
  return (gameState.raw?.cars?.owned ?? []).find((c) => c.id === id) ?? (typeof car === 'object' ? car : null);
}

function bodyLines(car) {
  const ev = car.fuel_kind === 'ev';
  const lines = [
    `${car.name_pl} · ${car.plate}`,
    FUEL_LINE[car.fuel_kind] ?? '{fuel} paliwo',
    `${ev ? 'Bateria' : 'Paliwo'}: ${num(car.fuel_pct)} % · zasięg ${num(car.range_km)} km`,
    `Przebieg: ${grouped((car.odometer_m ?? 0) / 1000)} km`,
  ];
  const ch = car.charging;
  if (ev && ch?.active) {
    lines.push('');
    lines.push(`{plug} Ładowanie: ${num(ch.pct)} % {arrow_right} do ${car.charge_limit ?? 80} %: ${formatEta(ch.eta_seconds)}`);
    lines.push(`Koszt dotąd: ${num(ch.kwh_added, 1)} kWh · ${grouped(ch.cost_so_far)} zł`);
  }
  if (ev) lines.push('Ładowanie do 80 % jest zdrowsze dla baterii {battery}');
  if (car.broken) {
    const cost = gameState.raw?.catalog?.fuel?.misfuel_repair ?? 1500;
    lines.push(`{wrench} Zepsute — napraw w serwisie (${grouped(cost)} zł)`);
  }
  return lines.join('\n');
}

export async function showGaragePanel(scene, car) {
  const rec = currentRecord(car);
  if (!rec) return;

  const ev = rec.fuel_kind === 'ev';
  const limit = rec.charge_limit === 100 ? 100 : 80;
  const actions = [{
    label: '{car} Jedź',
    disabled: !!rec.broken,
    fn: async () => {
      try {
        await gameState.carsLeaveGarage(rec.id);
        return 'drive';
      } catch (e) {
        showToast(scene, messageFor(e));
        return null;
      }
    },
  }];
  if (ev) {
    actions.push({
      label: `{battery} Limit ładowania: ${limit} % {arrow_right} ${limit === 80 ? 100 : 80} %`,
      fn: async () => {
        try {
          await gameState.carsSetChargeLimit(rec.id, limit === 80 ? 100 : 80);
          return 'reopen';
        } catch (e) {
          showToast(scene, messageFor(e));
          return null;
        }
      },
    });
  }

  const out = await showActionsModal(scene, {
    title: '{car} Twoje auto',
    body: bodyLines(rec),
    actions,
  });

  // Both follow-ups run only after the modal has closed itself: re-opening from inside the
  // action would stack two modals, and starting the street scene would tear this one down
  // mid-callback.
  if (out === 'reopen') return showGaragePanel(scene, rec.id);
  if (out === 'drive') scene.scene.start(SCENE.OUTSIDE, { spawnX: STREET_SPAWNS.HOUSE, drivingCarId: rec.id });
  return undefined;
}
